/**
 * Le coût de revient, vu par l'écran — le calcul, hors de React.
 *
 * Même découpe que `ot.ts`, `emballage.ts`, `affretement.ts` et `litiges.ts`,
 * pour la même raison : ce qui est ici se teste sans navigateur, sans base et
 * sans session.
 *
 *
 * CE FICHIER NE RECOMPTE PAS LES JOURS
 * ---------------------------------------------------------------------------
 * `jours_exploites` vient de la base, compté sur un VRAI calendrier par
 * `generate_series`. Le refaire ici ferait deux calculs du même nombre, et
 * deux calculs finissent par diverger — sur février d'une année bissextile,
 * par exemple.
 *
 * Ce fichier DIVISE, MET EN MOTS, et dit ce qui manque.
 *
 *
 * IL NE POSE AUCUNE VALEUR PAR DÉFAUT
 * ---------------------------------------------------------------------------
 * Pas de prix, pas d'amplitude, pas de consommation. Chaque fonction rend
 * `null` quand la donnée manque, et l'écran écrit alors ce qui manque plutôt
 * qu'un chiffre inventé. C'est la règle du remplissage d'une tournée, qui se
 * tait quand les dimensions sont inconnues.
 */

import type { RessourceFinance, SorteRessource, UniteFrais } from './api.js';

/**
 * LE COÛT JOURNALIER : prix mensuel ÷ jours exploités DANS LE MOIS CONSIDÉRÉ.
 *
 * Il varie donc d'un mois sur l'autre, et c'est assumé par le cadrage §2.1 :
 * une société qui exploite 7 j/7 obtient mécaniquement un coût journalier plus
 * bas — le même loyer se répartit sur plus de jours, et c'est juste.
 *
 * `null` si le prix manque, ou si aucun jour n'est exploité : diviser par zéro
 * n'a pas de réponse, et poser zéro en donnerait une fausse.
 */
export function coutJournalier(
  prixMensuel: string | null,
  joursExploites: number | null,
): number | null {
  if (prixMensuel === null || joursExploites === null || joursExploites <= 0) {
    return null;
  }
  const prix = Number(prixMensuel);
  if (!Number.isFinite(prix)) return null;
  return prix / joursExploites;
}

/**
 * LE COÛT HORAIRE : coût journalier ÷ amplitude de travail.
 *
 * `null` tant que l'amplitude n'est pas réglée. C'est le cas au démarrage de
 * toute société, et l'écran doit le DIRE : poser dix heures d'office ferait un
 * coût horaire faux que rien ne signalerait.
 */
export function coutHoraire(
  journalier: number | null,
  amplitude: string | null,
): number | null {
  if (journalier === null || amplitude === null) return null;
  const heures = Number(amplitude);
  if (!Number.isFinite(heures) || heures <= 0) return null;
  return journalier / heures;
}

/** « 1 234,56 € ». `null` reste `null` — on n'écrit pas « 0,00 € » pour rien. */
export function euros(montant: number | null, decimales = 2): string | null {
  if (montant === null || !Number.isFinite(montant)) return null;
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: decimales,
    maximumFractionDigits: decimales,
  }).format(montant);
}

/** Les sept jours, dans l'ordre où la semaine se lit, et leur initiale. */
export const SEMAINE = [
  { cle: 'lundi', lettre: 'L', nom: 'lundi' },
  { cle: 'mardi', lettre: 'M', nom: 'mardi' },
  { cle: 'mercredi', lettre: 'M', nom: 'mercredi' },
  { cle: 'jeudi', lettre: 'J', nom: 'jeudi' },
  { cle: 'vendredi', lettre: 'V', nom: 'vendredi' },
  { cle: 'samedi', lettre: 'S', nom: 'samedi' },
  { cle: 'dimanche', lettre: 'D', nom: 'dimanche' },
] as const;

export type CleJour = (typeof SEMAINE)[number]['cle'];

/**
 * LE CALENDRIER EN TOUTES LETTRES : « lundi au vendredi », « lundi au samedi ».
 *
 * Une PLAGE quand les jours cochés se suivent, la liste sinon. Écrire
 * « L M M J V » demande de compter les lettres ; « lundi au vendredi » se lit.
 *
 * `null` quand aucun coût n'est saisi — il n'y a alors pas de calendrier.
 */
export function calendrierDit(r: {
  readonly lundi: boolean | null;
  readonly mardi: boolean | null;
  readonly mercredi: boolean | null;
  readonly jeudi: boolean | null;
  readonly vendredi: boolean | null;
  readonly samedi: boolean | null;
  readonly dimanche: boolean | null;
}): string | null {
  if (r.lundi === null) return null;
  const coches = SEMAINE.filter((j) => r[j.cle] === true);
  if (coches.length === 0) return null;
  if (coches.length === 7) return 'tous les jours';
  if (coches.length === 1) return coches[0]?.nom ?? null;

  // Une plage : les jours cochés se suivent dans la semaine.
  const rangs = coches.map((j) => SEMAINE.findIndex((s) => s.cle === j.cle));
  const suivis = rangs.every((r2, i) => i === 0 || r2 === (rangs[i - 1] as number) + 1);
  if (suivis) {
    return `${coches[0]?.nom ?? ''} au ${coches[coches.length - 1]?.nom ?? ''}`;
  }
  return coches.map((j) => j.nom).join(', ');
}

/** « par mois », « au kilomètre », « à l'heure ». */
export function uniteDite(unite: UniteFrais): string {
  if (unite === 'mois') return 'par mois';
  if (unite === 'km') return 'au kilomètre';
  return 'à l’heure';
}

/**
 * Le nombre de décimales à montrer pour un frais.
 *
 * QUATRE au kilomètre : « 0,0320 €/km » de pneumatiques est un chiffre
 * courant, et arrondi à 0,03 il se trompe de 6 %. Deux ailleurs — personne
 * n'écrit une assurance à quatre décimales.
 */
export function decimalesDe(unite: UniteFrais): number {
  return unite === 'km' ? 4 : 2;
}

export function sorteDite(sorte: SorteRessource): string {
  if (sorte === 'vehicule') return 'Camion';
  if (sorte === 'remorque') return 'Remorque';
  return 'Chauffeur';
}

/**
 * CE QUE L'ÉCRAN FAIT APPARAÎTRE, et qui n'est dans aucune ligne.
 *
 * « 12 ressources — 4 sans coût saisi, aucun coût journalier ne se calcule pour
 * elles. »
 *
 * Même esprit que le « 5 dues sans bon » de l'emballage et le « 3 ordres
 * bloqués » des litiges : l'écran vaut par ce qu'il fait remarquer. Sans cette
 * phrase, quatre camions sans prix passeraient inaperçus au milieu de douze
 * lignes, et le coût du mois serait faux sans que rien ne le dise.
 *
 * Les ressources DÉSACTIVÉES ne comptent pas : un camion sorti du parc n'a pas
 * à porter un prix.
 */
export function decompteRessources(
  ressources: readonly RessourceFinance[],
): string {
  const vivantes = ressources.filter((r) => r.actif);
  if (vivantes.length === 0) {
    return 'Aucune ressource active. Ajoutez vos camions, remorques et chauffeurs dans Ressources.';
  }
  const sans = vivantes.filter((r) => r.prix_mensuel === null).length;
  const combien =
    vivantes.length > 1
      ? `${String(vivantes.length)} ressources`
      : '1 ressource';
  if (sans === 0) {
    return `${combien} — toutes ont un coût saisi.`;
  }
  const manque =
    sans > 1
      ? `${String(sans)} sans coût saisi`
      : '1 sans coût saisi';
  return `${combien} — ${manque}, aucun coût journalier ne se calcule pour ${
    sans > 1 ? 'elles' : 'elle'
  }.`;
}

/**
 * L'indice gazole est-il celui du mois qu'on regarde ?
 *
 * TÂCHE MENSUELLE DE L'ÉDITEUR, dit le cadrage §3. Si le mois n'est pas publié,
 * chaque société calcule avec un prix périmé SANS LE SAVOIR. L'écran doit donc
 * dire quel mois il emploie, et signaler qu'il date.
 */
export function gazoleDit(
  indices: readonly { readonly mois: string; readonly prix_calcule: string | null }[],
  moisRegarde: string,
): { texte: string; perime: boolean } {
  const dernier = indices[0];
  if (dernier === undefined) {
    return {
      texte: 'Aucun indice gazole publié : le coût carburant ne se calcule pas.',
      perime: true,
    };
  }
  /**
   * LE PRIX AFFICHÉ EST CELUI QUI SERT AU COÛT : le taux du mois (lot 137,
   * indice de base + indexation). Sans taux, il n'y a pas de prix.
   */
  const prixDit = (p: string | null): string =>
    p === null ? 'taux du mois absent' : `${euros(Number(p), 4) ?? '—'} le litre`;
  const exact = indices.find((i) => i.mois === moisRegarde);
  if (exact !== undefined) {
    return {
      texte: `Gazole ${moisRegarde} : ${prixDit(exact.prix_calcule)}.`,
      perime: exact.prix_calcule === null,
    };
  }
  return {
    texte: `Aucun indice pour ${moisRegarde} — le dernier publié est celui de ${
      dernier.mois
    }, ${dernier.prix_calcule === null ? 'sans taux' : `à ${prixDit(dernier.prix_calcule)}`}.`,
    perime: true,
  };
}

/** « 2026-09 » → « septembre 2026 ». */
export function moisDit(mois: string): string {
  const morceaux = /^(\d{4})-(\d{2})$/.exec(mois);
  if (morceaux === null) return mois;
  const noms = [
    'janvier',
    'février',
    'mars',
    'avril',
    'mai',
    'juin',
    'juillet',
    'août',
    'septembre',
    'octobre',
    'novembre',
    'décembre',
  ];
  const rang = Number(morceaux[2]) - 1;
  const nom = noms[rang];
  return nom === undefined ? mois : `${nom} ${morceaux[1] as string}`;
}

/** Le mois courant, au format « AAAA-MM ». */
export function moisCourant(horloge: Date = new Date()): string {
  const m = String(horloge.getMonth() + 1).padStart(2, '0');
  return `${String(horloge.getFullYear())}-${m}`;
}

/** Le mois d'avant ou d'après, sans jamais passer par un 31 qui n'existe pas. */
export function moisDecale(mois: string, pas: number): string {
  const morceaux = /^(\d{4})-(\d{2})$/.exec(mois);
  if (morceaux === null) return mois;
  const d = new Date(Date.UTC(Number(morceaux[1]), Number(morceaux[2]) - 1 + pas, 1));
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  return `${String(d.getUTCFullYear())}-${m}`;
}

/** La saisie de la boîte « coût d'une ressource ». */
export interface SaisieCout {
  readonly sorte: SorteRessource;
  readonly id: string;
  readonly nom: string;
  readonly prixMensuel: string;
  readonly consommation: string;
  readonly du: string;
  readonly jours: Readonly<Record<CleJour, boolean>>;
}

/** Ce que la boîte refuse, dit en français — avant tout appel à l'API. */
export function refusCout(saisie: {
  readonly prixMensuel: string;
  readonly du: string;
  readonly jours: Readonly<Record<CleJour, boolean>>;
}): string | null {
  const prix = saisie.prixMensuel.trim().replace(',', '.');
  if (prix === '') return 'Le prix mensuel est obligatoire.';
  if (!/^[0-9]{1,10}(\.[0-9]{1,2})?$/.test(prix)) {
    return 'Le prix mensuel est un montant positif, deux décimales au plus.';
  }
  if (!/^\d{4}-\d{2}-\d{2}$/.test(saisie.du)) {
    return 'Donnez la date à partir de laquelle ce prix s’applique.';
  }
  if (!SEMAINE.some((j) => saisie.jours[j.cle])) {
    return 'Cochez au moins un jour : sans cela, le coût journalier ne se calcule pas.';
  }
  return null;
}

/** Ce que la route attend, composé de la saisie. */
export function corpsCout(saisie: SaisieCout): Record<string, string | boolean> {
  const colonne =
    saisie.sorte === 'vehicule'
      ? 'vehicule_id'
      : saisie.sorte === 'remorque'
        ? 'remorque_id'
        : 'chauffeur_id';
  const consommation = saisie.consommation.trim().replace(',', '.');
  return {
    [colonne]: saisie.id,
    prix_mensuel: saisie.prixMensuel.trim().replace(',', '.'),
    du: saisie.du,
    ...Object.fromEntries(SEMAINE.map((j) => [j.cle, saisie.jours[j.cle]])),
    // LA CONSOMMATION N'A DE SENS QUE SUR UN VÉHICULE : l'envoyer ailleurs
    // ferait refuser l'écriture par la base, avec une erreur brute.
    ...(consommation === '' || saisie.sorte !== 'vehicule'
      ? {}
      : { consommation }),
  };
}

// ---------------------------------------------------------------------------
// Lot 2 : ce qu'un transport a coûté
// ---------------------------------------------------------------------------

/**
 * LE TEMPS PASSÉ, EN MINUTES, lu de « 7 h 45 » ou de « 7:45 » ou de « 465 ».
 *
 * Trois écritures acceptées parce que trois personnes différentes le saisiront,
 * et qu'aucune n'a à connaître celle des deux autres. Un nombre seul est pris
 * pour des MINUTES — c'est l'unité de la colonne, et « 480 » veut dire huit
 * heures pour qui tape vite.
 *
 * `null` quand ce n'est rien de tout cela : l'écran refuse alors, plutôt que
 * d'enregistrer un chiffre qu'il aurait deviné.
 */
export function minutesSaisies(texte: string): number | null {
  const propre = texte.trim().toLowerCase().replace(',', '.');
  if (propre === '') return null;

  const enHeuresEtMinutes = /^(\d{1,3})\s*(?:h|:)\s*(\d{1,2})?$/.exec(propre);
  if (enHeuresEtMinutes !== null) {
    const heures = Number(enHeuresEtMinutes[1]);
    const minutes = Number(enHeuresEtMinutes[2] ?? '0');
    if (minutes > 59) return null;
    const total = heures * 60 + minutes;
    return total > 0 ? total : null;
  }

  if (/^\d{1,5}$/.test(propre)) {
    const total = Number(propre);
    return total > 0 ? total : null;
  }
  return null;
}

/** 465 → « 7 h 45 ». Ce qui se saisit se relit sous la même forme. */
export function minutesDites(minutes: number | null): string | null {
  if (minutes === null || minutes <= 0) return null;
  const heures = Math.floor(minutes / 60);
  const reste = minutes % 60;
  if (heures === 0) return `${String(reste)} min`;
  if (reste === 0) return `${String(heures)} h`;
  return `${String(heures)} h ${String(reste).padStart(2, '0')}`;
}

/**
 * LA MARGE RÉELLE : ce que le client paie, moins ce que le transport a coûté.
 *
 * DISTINCTE DE LA MARGE D'AFFRÈTEMENT, qui compare le prix de vente au prix
 * d'achat d'un sous-traitant. Ici on compare au coût de revient de nos propres
 * moyens. Les deux ne répondent pas à la même question, et les confondre
 * ferait croire qu'un transport fait en propre n'a pas de coût.
 *
 * Rendue `null` tant que le coût n'est pas figé : une marge calculée sur un
 * coût incomplet serait plus flatteuse que la réalité, et rien ne le dirait.
 */
export interface MargeReelle {
  readonly montant: number;
  readonly taux: number;
  readonly couleur: 'vert' | 'orange' | 'rouge';
}

export function margeReelle(
  prixVente: string | null,
  cout: number | null,
): MargeReelle | null {
  if (prixVente === null || cout === null) return null;
  const vente = Number(prixVente);
  if (!Number.isFinite(vente) || vente <= 0 || !Number.isFinite(cout)) return null;
  const montant = vente - cout;
  const taux = (montant / vente) * 100;
  return {
    montant,
    taux,
    // MÊMES TROIS CRANS QUE LA MARGE D'AFFRÈTEMENT : sous zéro c'est une perte,
    // sous 10 % c'est un transport qui ne paie pas sa structure.
    couleur: montant < 0 ? 'rouge' : taux < 10 ? 'orange' : 'vert',
  };
}

export function margeReelleDite(m: MargeReelle): string {
  const somme = new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(m.montant);
  const taux = new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(m.taux);
  const signe = m.montant > 0 ? '+' : '';
  return `${signe}${somme} € · ${taux} %`;
}

/**
 * CE QUI MANQUE, dit en une phrase.
 *
 * L'API rend une liste ; l'écran en fait une phrase qui se lit. Vide, elle rend
 * `null` — il n'y a alors rien à signaler.
 */
/**
 * CE QUI MANQUE POUR CALCULER LA SURCHARGE GASOIL — 15/09/2026.
 *
 * DISTINCT DE `manquesDits`, ET C'EST UN DÉFAUT CONSTATÉ AU RENDU. L'écran de
 * l'ordre réemployait `manquesDits`, qui commence par « Le coût ne se calcule
 * pas » : sur le bloc d'indexation, cela annonçait un problème de COÛT là où
 * c'est la VENTE qui est en cause. Deux choses sans rapport, et le lecteur
 * serait allé chercher un prix de camion.
 */
export function manquesDIndexationDits(manques: readonly string[]): string | null {
  if (manques.length === 0) return null;
  if (manques.length === 1) {
    return `La surcharge gasoil ne se calcule pas : il manque ${manques[0] as string}.`;
  }
  const derniers = manques.slice(0, -1).join(', ');
  return `La surcharge gasoil ne se calcule pas : il manque ${derniers} et ${
    manques[manques.length - 1] as string
  }.`;
}

/**
 * UN NOMBRE GASOIL, À LA VIRGULE FRANÇAISE ET À QUATRE DÉCIMALES.
 *
 * `numeric(6, 4)` et `numeric(10, 4)` rendent « 1.4210 » et « 1.2875 » : un
 * point, et des zéros de remplissage. Ces valeurs sont CONTRACTUELLES —
 * Mohamed doit relire à l'écran exactement ce qu'il a publié.
 *
 * QUATRE DÉCIMALES TOUJOURS, zéros de fin compris. Un « 1,421 » pour un prix
 * publié à 1,4210 lui enlèverait le moyen de vérifier sa saisie, et une
 * colonne aux longueurs inégales ne se compare pas.
 *
 * ICI ET NON DANS L'ÉCRAN DES PARAMÈTRES, où elle était née le 15/09 : deux
 * écrans l'emploient désormais — la publication du mois et l'indexation d'un
 * ordre. La laisser dans l'un des deux ferait dépendre la fiche d'un ordre
 * d'un onglet de paramétrage.
 */
export function enQuatreDecimales(valeur: string | null): string {
  if (valeur === null) return '—';
  // LE VIDE N'EST PAS ZÉRO, et `Number('')` vaut 0 — défaut trouvé par
  // `gazole-affichage.test.ts`. Sans cette ligne, une valeur absente
  // s'afficherait « 0,0000 » : un prix du gazole publié à zéro.
  if (valeur.trim() === '') return valeur;
  const n = Number(valeur);
  if (!Number.isFinite(n)) return valeur;
  return new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  }).format(n);
}

export function manquesDits(manques: readonly string[]): string | null {
  if (manques.length === 0) return null;
  if (manques.length === 1) {
    return `Le coût ne se calcule pas : il manque ${manques[0] as string}.`;
  }
  const derniers = manques.slice(0, -1).join(', ');
  return `Le coût ne se calcule pas : il manque ${derniers} et ${
    manques[manques.length - 1] as string
  }.`;
}
